import { Container, Graphics, type DestroyOptions } from 'pixi.js';
import type { BackgroundContent, BookPageData, ImageLoadFailureKind } from '@/types';
import { runThreadPool } from '@/utils/threadPool';
import { createAction } from '../models/creators';
import gsap from 'gsap';
import type { Action } from '../../timeline/action';
import { SoundElement, BackgroundAsset, EventElement } from '../element';
import { ELEMENT_TYPE } from '@/constants';
import { Watermark } from '../watermark';

type PageModeType = 'edit' | 'view';
const READER_IMAGE_LOAD_TIMEOUT_MS = 15000;

export type BookPageLoadResult =
  | { status: 'loaded' }
  | { status: 'aborted' }
  | { status: 'failed'; failureKind: ImageLoadFailureKind };

export class BookPage extends Container {
  protected _data: BookPageData | null = null;

  protected _mode: PageModeType = 'view';

  protected _tween: gsap.core.Timeline | null = null;

  protected container: Container;
  protected bgContainer: Container;
  protected contentContainer: Container;
  protected fgContainer: Container;

  protected backgroundAsset: BackgroundAsset | null = null;

  protected actionMap: Map<string, Action> = new Map();

  protected pageMask: Graphics | null = null;

  /** 页面内部裁剪遮罩开关，默认开启 */
  protected _maskEnabled = true;

  protected watermark: Watermark | null = null;

  /** bakeDisplaySize 写入的显示缩放比，供 redrawWatermark 使用 */
  protected _displayScale = 1;

  constructor(data: BookPageData) {
    super();
    this._data = data;

    // 注意：不要在此处设置 isRenderGroup。
    // 当 BookPage 通过 updatePageScale 缩放时，渲染组会将子树渲染到中间纹理中，
    // 然后对该纹理进行 GPU 降采样——没有任何预过滤（mipmap），导致图片产生锯齿。
    // 移除渲染组后，缩放会直接作用于每个子元素的 worldTransform，
    // 精灵纹理由 GPU 直接以双线性过滤采样，得到更平滑的结果。
    // 模板遮罩（pageMask）在 v8 中不需要渲染组：因为遮罩和被遮罩的容器
    // 是同一父级下的兄弟节点，在同一个渲染 pass 内工作。
    this.container = new Container({ label: 'page' });
    this.bgContainer = new Container({ label: 'page-bg' });
    this.contentContainer = new Container({ label: 'page-content' });
    this.fgContainer = new Container({ label: 'page-fg' });

    this.addChild(this.container);
    this.container.addChild(this.bgContainer, this.contentContainer, this.fgContainer);

    this.init();
  }

  protected init() {
    this.initBackground();
    this.initMask();

    this.redraw();
  }

  protected initBackground() {
    if (!this.backgroundAsset) {
      this.backgroundAsset = new BackgroundAsset(this.pageWidth, this.pageHeight);
    }

    this.bgContainer.addChild(this.backgroundAsset);
  }

  /**
   * 加载/更新页背景
   * @param data 背景数据
   */
  public async loadBackground(data: BackgroundContent): Promise<boolean> {
    if (!this.backgroundAsset) {
      this.backgroundAsset = new BackgroundAsset();
      this.bgContainer.addChild(this.backgroundAsset);
    }

    return this.backgroundAsset.load(data, this.pageWidth, this.pageHeight);
  }

  protected initMask() {
    if (!this.pageMask) this.pageMask = new Graphics();
    // 将遮罩作为 container 的第一个子元素（在 PixiJS v8 中，模板遮罩推荐放在被遮罩容器内部）
    this.container.addChildAt(this.pageMask, 0);
    this.container.mask = this.pageMask;
  }

  /**
   * 设置页面裁剪遮罩开关。
   * 编辑器中为便于查看超界元素，可关闭此遮罩。
   * 关闭时需同步隐藏 pageMask 节点，否则其 #000 填充会作为可见子元素遮挡页面内容。
   * @param enabled - true 开启裁剪（默认），false 关闭裁剪
   */
  public setMaskEnabled(enabled: boolean): void {
    if (this._maskEnabled === enabled) return;
    this._maskEnabled = enabled;
    if (!this.container) return;
    this.container.mask = enabled ? this.pageMask : null;
    if (this.pageMask) {
      this.pageMask.visible = enabled;
    }
  }

  /** 设置水印显隐；尾页（backCover）不加水印 */
  public setWatermark(show: boolean) {
    // 步骤1：尾页跳过水印
    if (this._data?.type === 'backCover') return;

    // 步骤2：按需创建或销毁水印
    if (show && !this.watermark) {
      this.watermark = new Watermark();
      this.fgContainer.addChild(this.watermark);
      const region = this.getImageRegion();
      this.watermark.resize(region, this._data?.side, this._displayScale);
    } else if (!show && this.watermark) {
      this.watermark.destroy();
      this.watermark = null;
    }
  }

  public initTween(): void {
    this._tween = this._tween || gsap.timeline(); // 初始化时，不能paused，不然插入主时间轴时，控制不了
    this._tween.clear();
    this._tween.timeScale(1);
    this._tween.startTime(this._data?.startTime ?? 0);

    this.actionMap.forEach((action) => {
      if (action.tween) this._tween?.add(action.tween, action.startTime);
    });
  }

  public async load(signal?: AbortSignal): Promise<BookPageLoadResult> {
    this.destroyActions();

    const actions = this._data?.actions;

    // 无可加载内容 ≠ 插画失败，避免误显失败态
    if (!actions || !actions.length) return { status: 'loaded' };

    let imageFailureKind: ImageLoadFailureKind | null = null;
    const recordImageFailure = (kind: ImageLoadFailureKind) => {
      if (kind === 'network' || imageFailureKind === null) {
        imageFailureKind = kind;
      }
    };

    try {
      await runThreadPool(actions, async (data) => {
        // 排队中的任务在开始前若已被取消，直接跳过，避免无意义的创建开销
        if (signal?.aborted) return;

        // 封面旁白同样默认走 Web Audio（XHR 下载不受移动端手势限制，失败时 Howl 内部会降级 HTML5），
        // 避免移动端无手势时 HTML5 Audio 不预加载导致首页 loading 卡死
        const action = await createAction(data, {
          signal,
          imageTimeoutMs: signal ? READER_IMAGE_LOAD_TIMEOUT_MS : undefined,
          onImageLoadFailure: recordImageFailure
        });

        if (signal?.aborted) {
          action?.destroy();
          return;
        }

        // 图片元素创建失败时记失败；文本/音效失败不触发整页失败态
        if (!action && data.element?.type === ELEMENT_TYPE.IMAGE) {
          recordImageFailure('permanent');
          return;
        }

        this.addAction(action);
      });

      if (signal?.aborted) return { status: 'aborted' };
      return imageFailureKind
        ? { status: 'failed', failureKind: imageFailureKind }
        : { status: 'loaded' };
    } catch (error) {
      console.log(error);
      if (signal?.aborted) return { status: 'aborted' };
      return { status: 'failed', failureKind: imageFailureKind ?? 'permanent' };
    }
  }

  public addAction(action: Action | null) {
    if (!action || !action.element) return;

    this.actionMap.set(action.id, action);

    action.init();

    if (action.tween) this._tween?.add(action.tween, action.startTime);

    this.updateDuration();

    const element = action.element;

    if (element instanceof SoundElement) return;

    if (element instanceof EventElement || this._mode === 'edit') {
      element.eventMode = 'static';
    }

    if (
      element.zIndex === undefined ||
      element.zIndex < 0 ||
      element.zIndex >= this.contentContainer.children.length
    ) {
      this.contentContainer.addChild(element);
    } else {
      this.contentContainer.addChildAt(element, element.zIndex);
    }
  }

  public removeAction(action: Action) {
    if (action.tween) this._tween?.remove(action.tween);

    if (action.element) {
      action.element.parent?.removeChild(action.element);
    }

    this.actionMap.delete(action.id);

    action.destroy();

    this.updateDuration();
  }

  public updateMode(mode: PageModeType) {
    this._mode = mode;
    this.actionMap.forEach((action) => {
      const element = action.element;
      if (!element || element instanceof SoundElement) return;
      element.eventMode = element instanceof EventElement || mode === 'edit' ? 'static' : 'none';
    });
  }

  public updateDuration() {
    if (!this._data) return;
    let result = 0;

    this.actionMap.forEach((action) => {
      result = Math.max(result, action.startTime + action.duration);
    });

    this._data.duration = result;
  }

  /**
   * 设置固定的时间，这个方法会让page里面的所有action时间都是duration
   * 主要用于全屏翻页让没有音频的page有固定的时间
   * @param duration
   */
  public setStaticDuration(duration: number) {
    this.actionMap.forEach((action) => {
      action.duration = duration;
    });

    this.updateDuration();
  }

  public hide() {
    this.visible = false;
  }

  public show() {
    this.visible = true;
  }

  public showWatermark() {
    if (this.watermark && !this.watermark.visible) {
      this.watermark.visible = true;
    }
  }

  public hideWatermark() {
    if (this.watermark && this.watermark.visible) {
      this.watermark.visible = false;
    }
  }

  /** 导出截图前隐藏交互按钮（如「再看一遍」），避免出现在 PDF/图片中 */
  public hideEventElements() {
    this.actionMap.forEach((action) => {
      if (action.element instanceof EventElement) {
        action.element.visible = false;
      }
    });
  }

  /** 导出截图后恢复交互按钮可见性 */
  public showEventElements() {
    this.actionMap.forEach((action) => {
      if (action.element instanceof EventElement) {
        action.element.visible = true;
      }
    });
  }

  public updatePageScale(value: number) {
    this.scale.set(value);
  }

  /**
   * 阅读端加载前调用：将 page 数据"烘焙"到实际显示尺寸（修改 layout，不动 attrs）。
   * unbakeDisplaySize 在卸页时恢复。
   */
  public bakeDisplaySize(displayW: number, displayH: number): void {
    if (!this._data) return;

    const scale = displayW / (this._data.originWidth || this._data.width || 1);

    this._displayScale = scale;

    this._data.displayWidth = displayW;
    this._data.displayHeight = displayH;

    this._data.actions?.forEach((actionData) => {
      const attrs = actionData.element?.attrs;
      const layout = attrs?.layout;
      if (layout) {
        layout.parentWidth = displayW;
        layout.parentHeight = displayH;
        layout.displayScale = scale;
      }

      // 迁移旧数据：没有 originX/originY 时用当前 attrs.x/y 作为设计稿原始坐标
      if (attrs) {
        if (attrs.originX === undefined) attrs.originX = attrs.x;
        if (attrs.originY === undefined) attrs.originY = attrs.y;
      }
    });

    this.redraw();
  }

  public resize(width: number, height: number) {
    if (!this._data) return;

    this._data.width = width;
    this._data.height = height;

    this.redraw();
  }

  protected redraw() {
    this.redrawBackground();
    this.redrawMask();
    this.redrawWatermark();
    this.actionMap.forEach((action) => {
      const element = action.element;
      if (!element || element instanceof SoundElement) return;
      element.refresh();
    });
  }

  protected redrawBackground() {
    if (!this.backgroundAsset) return;

    this.backgroundAsset.resize(this.pageWidth, this.pageHeight);
  }

  protected redrawMask() {
    if (!this.pageMask) return;

    this.pageMask.clear();
    this.pageMask
      .rect(-this.pageWidth / 2, -this.pageHeight / 2, this.pageWidth, this.pageHeight)
      .fill('#000000');
  }

  protected redrawWatermark() {
    if (!this.watermark) return;

    const region = this.getImageRegion();
    this.watermark.resize(region, this._data?.side, this._displayScale);
  }

  /**
   * 从 _data.actions 原始数据中获取第一个图片元素的位置区域
   * （不依赖已加载的 actionMap，因为 setWatermark 可能在元素异步加载之前调用），
   * 与页面区域取交集（防止图片超出页面导致水印被裁剪），
   * 若不存在图片元素则回退到整页区域
   */
  protected getImageRegion() {
    const pageRect = {
      left: -this.pageWidth / 2,
      right: this.pageWidth / 2,
      top: -this.pageHeight / 2,
      bottom: this.pageHeight / 2
    };

    const imgAction = this._data?.actions?.find((a) => a.element?.type === ELEMENT_TYPE.IMAGE);

    if (imgAction?.element?.attrs) {
      const attrs = imgAction.element.attrs;
      const layout = attrs.layout;
      const displayScale = layout?.displayScale ?? 1;
      const width = (attrs.width ?? 0) * displayScale;
      const height = (attrs.height ?? 0) * displayScale;
      let x = attrs.x ?? 0;
      let y = attrs.y ?? 0;

      if (layout?.left !== undefined && layout.parentWidth !== undefined) {
        x = -layout.parentWidth / 2 + layout.left * layout.parentWidth + width / 2;
      } else if (layout?.right !== undefined && layout.parentWidth !== undefined) {
        x = layout.parentWidth / 2 - layout.right * layout.parentWidth - width / 2;
      }
      if (layout?.top !== undefined && layout.parentHeight !== undefined) {
        y = -layout.parentHeight / 2 + layout.top * layout.parentHeight + height / 2;
      } else if (layout?.bottom !== undefined && layout.parentHeight !== undefined) {
        y = layout.parentHeight / 2 - layout.bottom * layout.parentHeight - height / 2;
      }

      const imgLeft = x - width / 2;
      const imgRight = x + width / 2;
      const imgTop = y - height / 2;
      const imgBottom = y + height / 2;

      // 取图片区域与页面区域的交集
      const left = Math.max(imgLeft, pageRect.left);
      const right = Math.min(imgRight, pageRect.right);
      const top = Math.max(imgTop, pageRect.top);
      const bottom = Math.min(imgBottom, pageRect.bottom);

      return {
        width: Math.max(right - left, 0),
        height: Math.max(bottom - top, 0),
        x: (left + right) / 2,
        y: (top + bottom) / 2
      };
    }

    // 回退：使用整页区域（原点居中）
    return { width: this.pageWidth, height: this.pageHeight, x: 0, y: 0 };
  }

  /**
   * 卸载已懒加载的页面内容（销毁 Action / 释放音频等资源），保留页结构与主时间轴上的
   * 占位 _tween，之后可再次 load() 重建。供阅读端滑动窗口卸载离开可视窗口的页，
   * 控制移动端内存与媒体资源占用。不回写 duration，理由见 destroyActions。
   */
  public unload(): void {
    this.destroyActions();
  }

  /**
   * 销毁页内全部 Action，但不回写 duration。
   * page.data 与 projectStore 共享引用；若清空后 updateDuration 把 duration 写成 0，
   * 下次重建时间轴会按 0 时长 + padding 排程，表现为播放过快。
   */
  protected destroyActions() {
    const actionsToLink = Array.from(this.actionMap.values());

    // 1. 逐个拆掉 tween / 显示对象并 destroy（不走 removeAction，避免 updateDuration）
    actionsToLink.forEach((action) => {
      if (action.tween) this._tween?.remove(action.tween);

      if (action.element) {
        action.element.parent?.removeChild(action.element);
      }

      this.actionMap.delete(action.id);
      action.destroy();
    });

    // 2. 清空映射
    this.actionMap.clear();
  }

  protected destroyTween() {
    this._tween?.kill();
    this._tween = null;
  }

  public override destroy(options?: DestroyOptions): void {
    this.destroyActions();
    this.destroyTween();
    super.destroy(options);
  }

  public get data() {
    return this._data;
  }

  public set data(data: BookPageData | null) {
    this._data = data;
  }

  public get pageId() {
    return this._data?.id ?? '';
  }

  public get pageIndex() {
    return this._data?.index ?? 0;
  }

  public get pageWidth() {
    return this._data?.displayWidth || this._data?.width || 0;
  }

  public get pageHeight() {
    return this._data?.displayHeight || this._data?.height || 0;
  }

  public get actions() {
    return this.actionMap;
  }

  public get tween() {
    return this._tween;
  }

  public get duration() {
    return this.data?.duration ?? 0;
  }

  public get maskEnabled(): boolean {
    return this._maskEnabled;
  }
}
